import {
  Hash,
  Type,
  ToggleLeft,
  Calendar,
  Braces,
  List,
  Clock,
} from "lucide-react";

export const EXCLUDE_KEYS = [
  "uid",
  "isDecript",
  "isDecrypted",
  "is_decript",
  "title",
  "testConnectionBtn",
  "tabId",
  "configType",
  "onDelete",
  "onDataChange",
  "header",
  "borderColor",
  "shadowColor",
  "configData",
  "response",
  "nodeType",
  "type",
];

export const workflowTypes = [
  {
    label: "None",
    value: "none",
  },
  {
    label: "Save Logs",
    value: "save_log",
  },
  {
    label: "Save Datalist",
    value: "save_datalist",
  },
  {
    label: "Threat Lookup",
    value: "threatlookup",
  },
  {
    label: "Action",
    value: "action",
  },
  {
    label: "Settings",
    value: "settings",
  },
  {
    label: "Data Inventory",
    value: "dspm",
  },
  {
    label: "Identity Intelligence",
    value: "itdr",
  },
];

export const LS_PRODUCT_TYPE = [
  {
    label: "NDR",
    value: "ndr",
  },
  {
    label: "DSPM",
    value: "ddr",
  },
  {
    label: "ITDR",
    value: "itdr",
  },
  { label: "CMX", value: "cmx" },
];

export const extractJsonDataFromResponse = (
  result: any,
  nodeId?: string,
  nodeTitle?: string,
  subType?: string,
): any => {
  let extractedData: any = null;

  // Helper function to find node result with multiple criteria
  const findNodeResult = (nodeResults: any[]) => {
    return nodeResults.find(
      (nr: any) =>
        nr.node_id === nodeId ||
        nr.uid === nodeId ||
        nr.node_title === nodeTitle ||
        (subType && nr.sub_type === subType) ||
        (subType && nr.type === "save" && nr.sub_type === subType),
    );
  };

  // Helper function to extract json_data from output_data
  const extractFromOutputData = (
    outputData: any[],
    isIfElse: boolean = false,
  ) => {
    if (!outputData) return null;

    // For if_else nodes, output_data is [[trueResults], [falseResults]]
    if (isIfElse && Array.isArray(outputData) && outputData.length === 2) {
      // Extract json_data from both arrays
      const trueResults = Array.isArray(outputData[0])
        ? outputData[0].map((item: any) => item?.json_data || item)
        : [];
      const falseResults = Array.isArray(outputData[1])
        ? outputData[1].map((item: any) => item?.json_data || item)
        : [];

      return [trueResults, falseResults];
    }

    // Standard extraction for other nodes
    if (outputData?.[0]?.json_data) {
      return outputData.map((item: any) => item.json_data);
    } else if (outputData?.[0]) {
      return outputData;
    }
    return null;
  };

  // Try different response structures
  if (result?.body?.results?.[0]?.node_results) {
    // Structure: result.body.results[0].node_results
    const nodeResults = result.body.results[0].node_results;
    let nodeResult = findNodeResult(nodeResults);

    // If current node not found, use the last node (most recent execution)
    if (!nodeResult && nodeResults.length > 0) {
      nodeResult = nodeResults[nodeResults.length - 1];
    }

    const isIfElse =
      subType === "if_else" || nodeResult?.sub_type === "if_else";
    extractedData = extractFromOutputData(nodeResult?.output_data, isIfElse);
  } else if (result?.data?.results?.[0]?.node_results) {
    // Alternative structure with data wrapper
    const nodeResults = result.data.results[0].node_results;
    let nodeResult = findNodeResult(nodeResults);

    if (!nodeResult && nodeResults.length > 0) {
      nodeResult = nodeResults[nodeResults.length - 1];
    }

    const isIfElse =
      subType === "if_else" || nodeResult?.sub_type === "if_else";
    extractedData = extractFromOutputData(nodeResult?.output_data, isIfElse);
  } else if (result?.results?.[0]?.node_results) {
    // Another variant without body/data wrapper (used by SaveLogs/SaveDatalist)
    const nodeResults = result.results[0].node_results;
    let nodeResult = findNodeResult(nodeResults);

    if (!nodeResult && nodeResults.length > 0) {
      nodeResult = nodeResults[nodeResults.length - 1];
    }

    const isIfElse =
      subType === "if_else" || nodeResult?.sub_type === "if_else";
    extractedData = extractFromOutputData(nodeResult?.output_data, isIfElse);
  } else if (result?.body?.results?.[0]?.final_output) {
    // Fallback to final_output with body wrapper
    const finalOutput = result.body.results[0].final_output;
    if (Array.isArray(finalOutput)) {
      extractedData = finalOutput.map((item: any) => item.json_data || item);
    } else {
      extractedData = finalOutput;
    }
  } else if (result?.results?.[0]?.final_output) {
    // Fallback to final_output without body wrapper
    const finalOutput = result.results[0].final_output;
    if (Array.isArray(finalOutput)) {
      extractedData = finalOutput.map((item: any) => item.json_data || item);
    } else {
      extractedData = finalOutput;
    }
  }

  // If still no data extracted, return the raw result
  if (!extractedData) {
    extractedData = result?.body || result;
  }

  return extractedData;
};

/**
 * Unwraps json_data from response objects
 * Handles regular arrays, if_else format [[true], [false]], and single objects
 */
export const unwrapJsonData = (data: any): any => {
  if (!data) return data;

  if (Array.isArray(data)) {
    // Check if this is an if_else response format: [[trueItems], [falseItems]]
    const isIfElseFormat =
      data.length === 2 && Array.isArray(data[0]) && Array.isArray(data[1]);

    if (isIfElseFormat) {
      // Handle if_else response: unwrap json_data from both arrays
      return [
        data[0].map((item: any) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        }),
        data[1].map((item: any) => {
          if (item && typeof item === "object" && "json_data" in item) {
            return item.json_data;
          }
          return item;
        }),
      ];
    } else {
      // Regular array response
      return data.map((item) => {
        // Check if item has json_data wrapper structure
        if (item && typeof item === "object" && "json_data" in item) {
          return item.json_data;
        }
        return item;
      });
    }
  } else if (data && typeof data === "object" && "json_data" in data) {
    // Single object with json_data
    return data.json_data;
  }

  return data;
};

// Helper function to get icon component for data type
export const getDataTypeIcon = (dataType: string) => {
  const iconMap: { [key: string]: any } = {
    number: Hash,
    string: Type,
    boolean: ToggleLeft,
    date: Calendar,
    object: Braces,
    array: List,
    dateTime: Clock,
  };

  return iconMap[dataType] || Type;
};

export const handlerStyle =
  "!bg-background !w-4 !h-4 rounded-full !border-2 !border-muted-foreground";
